import React, { useState, useEffect, useCallback } from 'react';
import { calculateDistance } from './utils';
import config from '../config';
import useTranslation from './useTranslation';

const Icon = ({ name, className }) => (
  <span className={`material-symbols-outlined ${className || ''}`}>
    {name}
  </span>
);

const getTransportTypeInfo = (routeType) => {
  // GTFS route_type: 0=Tram, 1=Metro, 2=Rail, 3=Bus
  switch (parseInt(routeType)) {
    case 0: return { icon: 'tram', label: 'Tram' };
    case 1: return { icon: 'subway', label: 'Metro' };
    case 2: return { icon: 'train', label: 'Treno' };
    default: return { icon: 'directions_bus', label: 'Bus' };
  }
};

// Funzione di utilità per il parsing del CSV
const parseCSV = (text) => {
  const lines = text.split('\n');
  const headers = lines[0].split(',').map(h => h.trim());
  return lines.slice(1)
    .filter(line => line.trim())
    .map(line => {
      const values = line.split(',').map(v => v.trim());
      const item = {};
      headers.forEach((header, index) => {
        item[header] = values[index] || '';
      });
      return item;
    });
};

const TransportPanel = ({ isOpen, onClose, poi }) => {
  const { t } = useTranslation();
  const [transportRoutes, setTransportRoutes] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
      onClose();
    }, 300);
  };

  const fetchTransportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Carica il file delle fermate per le coordinate
      const stopsResponse = await fetch(`${config.transportPath}/stops.txt`);
      const stopsText = await stopsResponse.text();
      const stops = parseCSV(stopsText);

      // 2. Carica il JSON pre-processato
      const transportResponse = await fetch(`${config.transportPath}/transport-data.json`);
      const transportData = await transportResponse.json();

      // 3. Calcola le fermate più vicine
      const poiLat = parseFloat(poi.field_geolocalizzazione[0].lat);
      const poiLng = parseFloat(poi.field_geolocalizzazione[0].lng);

      const nearbyStops = stops
        .map(stop => ({
          ...stop,
          distance: calculateDistance(
            poiLat,
            poiLng,
            parseFloat(stop.stop_lat),
            parseFloat(stop.stop_lon)
          )
        }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 20);

      // 4. Crea la lista dei mezzi
      const routesList = [];
      const seenRoutes = new Set();

      nearbyStops.forEach(stop => {
        const stopRoutes = transportData[stop.stop_id];
        if (!stopRoutes) return;

        stopRoutes.routes.forEach(routeKey => {
          // Skip se abbiamo già visto questo mezzo
          if (seenRoutes.has(routeKey)) return;
          seenRoutes.add(routeKey);

          const [type, number] = routeKey.split('-');
          const directions = stopRoutes.directions[routeKey];
          
          routesList.push({
            type,
            number,
            direction: directions[0], // Prendi solo la prima direzione
            stopName: stop.stop_name,
            distance: stop.distance
          });
        });
      });

      // 5. Prendi i primi 20 mezzi
      setTransportRoutes(routesList.slice(0, 20));

    } catch (error) {
      console.error('Errore nel caricamento dei dati:', error);
      setError('Si è verificato un errore durante il caricamento delle fermate');
    } finally {
      setLoading(false);
    }
  }, [poi]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (poi?.field_geolocalizzazione?.[0]) {
        fetchTransportData();
      }
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, poi, fetchTransportData]);

  const panelClasses = [
    'focus-panel',
    isOpen ? 'is-open' : '',
    isAnimating ? 'is-closing' : '',
  ].join(' ');

  return (
    <div className={panelClasses}>
      <div className="sticky-top border-bottom bg-white">
        <div className="container d-flex justify-content-between align-items-center py-3">
          <button
            onClick={handleClose}
            aria-label="Chiudi"
            className="btn bg-white d-flex align-items-center justify-content-center ratio-1x1 goback"
            style={{ position: 'relative', top: 0, left: 0 }}
          >
            <Icon name="close" />
          </button>
          <div className="d-flex align-items-center">
            <Icon name="directions_bus" className="me-2" />
           
          </div>
        </div>
      </div>

      <div className="container description text-content pb-0">
        <h1 className="mb-4 mt-4">{t('mezziVicinanze')}</h1>
        
        {loading && (
          <div className="d-flex flex-column align-items-center py-4">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">{t('caricamentoInCorso')}</span>
            </div>
            <p className="text-muted"> {t('ricercaMezziInCorso')}</p>
          </div>
        )}

        {error && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <Icon name="error" className="me-2" />
            <div>{error}</div>
          </div>
        )}

        {!loading && !error && transportRoutes.length === 0 && (
          <div className="alert alert-info d-flex align-items-center" role="alert">
            <Icon name="info" className="me-2" />
            <div>{t('nessunoMezzoVicino')}</div>
          </div>
        )}

        {!loading && !error && transportRoutes.length > 0 && (
          <div className="list-group w-100 mb-4">
            {transportRoutes.map((route, index) => {
              const transport = getTransportTypeInfo(route.type);
              return (
                <div 
                  key={index}
                  className={`list-group-item d-flex flex-column py-3 ${
                    index !== 0 ? 'border-top' : ''
                  }`}
                >
                  <div className="d-flex align-items-start w-100 text-start justify-content-start ">
                    <Icon name={transport.icon} className="me-2" />
                    <p className="fw-semibold">
                      {transport.label} {route.number}  {t('direzione')} {route.direction}
                    </p>
                  </div>
                  
                  <div className="d-flex w-100 mt-2 text-start justify-content-start">
                    <p className="fw-semibold mb-0">{t('fermata')} </p>
                    <p className="text-muted ms-1 mb-0">{route.stopName}</p>
                  </div>
                  
                  <div className="d-flex align-items-center w-100 mt-2 text-start justify-content-start">
                    <Icon name="near_me" className="me-1 text-muted" />
                    <p className="text-muted">
                    {t('distanzaFermata')} {(route.distance * 1000).toFixed(0)}m
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransportPanel;